/**
 * Finite
 * A reflective visualization of your life in weeks
 * 
 * Inspired by Tim Urban's "Your Life in Weeks" and Dieter Rams' design philosophy
 */

import './style.css';
import { createInputForm, FormData } from './components/InputForm';
import { createWeeksGrid } from './components/WeeksGrid';
import {
    createStatsCards,
    createReflectiveStats,
    createQuoteSection,
    createDataSource,
    createVizFooter
} from './components/LifeStats';
import { createSocietalSection, createCosmicSection } from './components/ExtendedStats';
import { getLifeExpectancy, lifeExpectancyData } from './data/lifeExpectancy';
import { getSunshineHours } from './data/sunshineData';
import { calculateLifeStats, calculateSocietalStats, calculateCosmicStats, LifeStats } from './utils/calculations';
import { BackgroundAnimation } from './components/BackgroundAnimation';
import { createShareModal } from './components/ShareModal';

class App {
    private container: HTMLElement;
    private formData: FormData | null = null;
    private currentStats: LifeStats | null = null;
    private currentQuote: { text: string; author: string } | null = null;

    constructor(containerId: string) {
        const container = document.getElementById(containerId);
        if (!container) {
            throw new Error(`Container element #${containerId} not found`);
        }
        this.container = container;
        new BackgroundAnimation();
        this.init();
    }

    private init(): void {
        this.showInputPage();
    }

    private clear(): void {
        this.container.innerHTML = '';
    }

    private showInputPage(): void {
        this.clear();

        const inputForm = createInputForm((data) => {
            this.formData = data;
            this.showVisualizationPage();
        });

        this.container.appendChild(inputForm);
    }

    private showVisualizationPage(): void {
        if (!this.formData) return;

        this.clear();

        // Get life expectancy
        const expectancyResult = getLifeExpectancy(
            this.formData.countryCode,
            this.formData.gender
        );

        // Fallback to world average if country not found
        const expectancyData = expectancyResult?.data || lifeExpectancyData[0];
        const baseExpectancy = expectancyResult?.value || 72.8;

        // Use custom expectancy if provided
        const lifeExpectancy = this.formData.customExpectancy || baseExpectancy;
        const isCustomExpectancy = this.formData.customExpectancy !== null;

        // Calculate stats
        const stats = calculateLifeStats(this.formData.birthDate, lifeExpectancy);
        this.currentStats = stats;

        // Phase 2: Calculate extended stats
        const birthYear = this.formData.birthDate.getFullYear();
        const sunshineHoursPerYear = getSunshineHours(this.formData.countryCode);
        const societalStats = calculateSocietalStats(stats.ageYears, lifeExpectancy);
        const cosmicStats = calculateCosmicStats(stats.ageYears, sunshineHoursPerYear);

        // Create visualization page
        const page = document.createElement('div');
        page.className = 'viz-page fade-in';

        // Header
        const header = document.createElement('div');
        header.className = 'viz-header';
        header.innerHTML = `
      <h1>Your life, visualized</h1>
      <p class="subtitle">
        ${stats.ageYears} years, ${stats.ageMonths} months, and ${stats.ageDays} days old
      </p>
    `;
        page.appendChild(header);

        // Content container
        const content = document.createElement('div');
        content.className = 'viz-content';

        // Stats cards
        const statsOptions = { stats, expectancyData, lifeExpectancy, isCustomExpectancy };
        content.appendChild(createStatsCards(statsOptions));

        // Data source
        content.appendChild(createDataSource(statsOptions));

        // Weeks grid
        content.appendChild(createWeeksGrid({ stats, animationDelay: 1 }));

        // Reflective stats
        content.appendChild(createReflectiveStats(stats));

        // Phase 2: Extended stats sections
        const extendedOptions = { societalStats, cosmicStats, birthYear };
        content.appendChild(createSocietalSection(extendedOptions));
        content.appendChild(createCosmicSection(extendedOptions));

        // Quote (store for sharing)
        const quoteSection = createQuoteSection();
        const quoteText = quoteSection.querySelector('.quote-text')?.textContent || '';
        const quoteAuthor = quoteSection.querySelector('.quote-attribution')?.textContent?.replace('— ', '') || '';
        this.currentQuote = { text: quoteText, author: quoteAuthor };
        content.appendChild(quoteSection);

        // Footer with share button
        content.appendChild(createVizFooter(
            () => this.showInputPage(),
            () => this.openShareModal()
        ));

        page.appendChild(content);
        this.container.appendChild(page);

        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    private openShareModal(): void {
        if (!this.currentStats) return;

        const modal = createShareModal({
            stats: this.currentStats,
            quote: this.currentQuote || undefined,
            onClose: () => {
                // Modal handles its own cleanup
            },
        });

        document.body.appendChild(modal);
    }
}

// Initialize app
document.addEventListener('DOMContentLoaded', () => {
    new App('app');
});
